import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

interface GetAppProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  getAppData?: any;
  loading?: boolean;
}

const GetApp: React.FC<GetAppProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  getAppData,
  loading,
}) => {
  useEffect(() => {
    if (getAppData) {
      form.setFieldsValue({
        getApp: {
          heading: getAppData.heading || "",
          description: getAppData.description || "",
          playStoreLink: getAppData.playStoreLink || "",
          appStoreLink: getAppData.appStoreLink || "",
          image: getAppData.image ? getAppData.image : "",
        },
      });
    } else {
      form.resetFields();
    }
  }, [getAppData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop Table */}
          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Image
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Heading
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Description
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Play Store
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    App Store
                  </th>
                  <th className="py-3 px-4 text-end text-sm md:text-base">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {getAppData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4 w-[120px]">
                      <Image
                        src={getAppData?.image || "/placeholder.jpg"}
                        height={50}
                        width={120}
                        className="object-contain w-full h-full"
                        alt="Get App"
                      />
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {getAppData?.heading || "—"}
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {getAppData?.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {getAppData?.playStoreLink || "—"}
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {getAppData?.appStoreLink || "—"}
                    </td>

                    <td className="py-3 px-4 text-primary">
                      <div className="flex items-center justify-end gap-4">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer hover:text-blue-600 text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={6} className="text-center py-6 text-gray-500">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden">
            {getAppData ? (
              <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Image:
                  </span>
                  <Image
                    src={getAppData?.image || "/placeholder.jpg"}
                    height={70}
                    width={70}
                    alt="Get App"
                  />
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Heading:
                  </span>
                  <p>{getAppData?.heading || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Description:
                  </span>
                  <p>{getAppData?.description || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Play Store:
                  </span>
                  <p>{getAppData?.playStoreLink || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    App Store:
                  </span>
                  <p>{getAppData?.appStoreLink || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-[#F9F9F9] border flex justify-center border-gray-200 rounded-lg p-4 shadow-sm">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["getApp", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["getApp", "description"]}
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input placeholder="Enter description" />
              </Form.Item>

              <Form.Item
                label="Play Store Link"
                name={["getApp", "playStoreLink"]}
                rules={[
                  { required: true, message: "Please enter play store link" },
                ]}
              >
                <Input placeholder="Enter play store link" />
              </Form.Item>

              <Form.Item
                label="App Store Link"
                name={["getApp", "appStoreLink"]}
                rules={[
                  { required: true, message: "Please enter app store link" },
                ]}
              >
                <Input placeholder="Enter app store link" />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["getApp", "image"]}
                rules={[{ required: true, message: "Please upload image" }]}
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>
            </Form>
          </div>

          <div className="flex justify-start gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetApp;